import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, MailIcon, ShieldCheckIcon } from 'lucide-react';
const PrivacyPolicyPage = () => {
  return <div className="min-h-screen bg-gray-50" data-id="element-3846">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-3847">
        {/* Header */}
        <div className="mb-12" data-id="element-3848">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6" data-id="element-3849">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-3850" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-3851">
            Privacy Policy
          </h1>
          <p className="text-gray-600" data-id="element-3852">Last Updated: January 17, 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12" data-id="element-3853">
          <div className="prose prose-lg max-w-none" data-id="element-3854">
            <p className="text-lg text-gray-700 leading-relaxed mb-8" data-id="element-3855">
              ShoPilots AI ("we," "us," or "our") is committed to protecting
              your privacy. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our Service.
            </p>
            <p className="text-gray-700 leading-relaxed mb-8" data-id="element-3856">
              Please read this Privacy Policy carefully. By using ShoPilots AI,
              you agree to the collection and use of information in accordance
              with this policy.
            </p>

            {/* Continue with all sections... */}
            <section className="mb-10" data-id="element-3857">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-3858">
                1. Information We Collect
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3859">
                1.1 Information You Provide to Us
              </h3>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-3860">
                Account Information:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3861">
                <li data-id="element-3862">Name and email address</li>
                <li data-id="element-3863">Company name and website URL</li>
                <li data-id="element-3864">Contact information</li>
                <li data-id="element-3865">
                  Payment information (processed through our third-party payment
                  processor, Paddle)
                </li>
              </ul>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-3866">
                Content and Data:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3867">
                <li data-id="element-3868">Website data you upload to create AI agents</li>
                <li data-id="element-3869">Content you input into the platform</li>
                <li data-id="element-3870">Training data and documents you provide</li>
                <li data-id="element-3871">Configuration settings and customizations</li>
                <li data-id="element-3872">
                  Chat conversations and AI interactions (may be stored
                  temporarily or permanently)
                </li>
              </ul>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-3873">
                Communications:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3874">
                <li data-id="element-3875">Messages you send us</li>
                <li data-id="element-3876">Support tickets and inquiries</li>
                <li data-id="element-3877">Feedback and survey responses</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3878">
                1.2 Information Collected Automatically
              </h3>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-3879">Usage Data:</p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3880">
                <li data-id="element-3881">Log data (IP address, browser type, operating system)</li>
                <li data-id="element-3882">Device information</li>
                <li data-id="element-3883">Pages visited and features used</li>
                <li data-id="element-3884">Time and date of visits</li>
                <li data-id="element-3885">Referring website addresses</li>
                <li data-id="element-3886">Click-stream data</li>
              </ul>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-3887">
                Cookies and Tracking Technologies:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3888">
                <li data-id="element-3889">
                  We use cookies, web beacons, and similar tracking technologies
                  to track activity on our Service
                </li>
                <li data-id="element-3890">
                  You can instruct your browser to refuse all cookies or to
                  indicate when a cookie is being sent
                </li>
                <li data-id="element-3891">
                  If you do not accept cookies, some portions of our Service may
                  not function properly
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3892">
                1.3 Information from Third Parties
              </h3>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-3893">
                AI Service Providers:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3894">
                <li data-id="element-3895">
                  We use OpenAI and Anthropic Claude to power our AI features
                </li>
                <li data-id="element-3896">
                  These providers may process your data according to their own
                  privacy policies
                </li>
                <li data-id="element-3897">
                  We may receive aggregated analytics from these providers
                </li>
              </ul>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-3898">
                Payment Processor:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700" data-id="element-3899">
                <li data-id="element-3900">Paddle processes payment information on our behalf</li>
                <li data-id="element-3901">
                  We do not store full credit card information on our servers
                </li>
              </ul>
            </section>

            <section className="mb-10" data-id="element-3902">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-3903">
                2. How We Use Your Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-3904">
                We use the information we collect for the following purposes:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3905">
                2.1 Service Provision
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4" data-id="element-3906">
                <li data-id="element-3907">To create, maintain, and operate your account</li>
                <li data-id="element-3908">To process your uploaded data and create AI agents</li>
                <li data-id="element-3909">To enable AI-powered conversations and interactions</li>
                <li data-id="element-3910">
                  To deploy your AI agents across various channels (website,
                  WhatsApp, Telegram, etc.)
                </li>
                <li data-id="element-3911">To provide customer support and respond to inquiries</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3912">
                2.2 Service Improvement
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4" data-id="element-3913">
                <li data-id="element-3914">To analyze usage patterns and improve our Service</li>
                <li data-id="element-3915">To develop new features and functionality</li>
                <li data-id="element-3916">To troubleshoot technical issues</li>
                <li data-id="element-3917">To understand how users interact with our platform</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3918">
                2.3 Communication
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4" data-id="element-3919">
                <li data-id="element-3920">To send you service-related announcements and updates</li>
                <li data-id="element-3921">To respond to your comments, questions, and requests</li>
                <li data-id="element-3922">
                  To send you technical notices, security alerts, and
                  administrative messages
                </li>
                <li data-id="element-3923">
                  To notify you about changes to our Terms or Privacy Policy
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3924">
                2.4 Business Operations
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4" data-id="element-3925">
                <li data-id="element-3926">To process payments and prevent fraud</li>
                <li data-id="element-3927">To comply with legal obligations</li>
                <li data-id="element-3928">To enforce our Terms of Service</li>
                <li data-id="element-3929">To protect our rights and property</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3930">
                2.5 Marketing (with your consent)
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-3931">
                <li data-id="element-3932">
                  To send you promotional communications about new features or
                  services
                </li>
                <li data-id="element-3933">You may opt out of marketing communications at any time</li>
              </ul>
            </section>

            <section className="mb-10" data-id="element-3934">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-3935">
                3. How We Share Your Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-3936">
                We may share your information in the following circumstances:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3937">
                3.1 Third-Party Service Providers
              </h3>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4" data-id="element-3938">
                <p className="font-semibold text-gray-900 mb-2" data-id="element-3939">
                  AI Providers (OpenAI, Anthropic Claude):
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700" data-id="element-3940">
                  <li data-id="element-3941">
                    Your uploaded data and conversations are processed by these
                    AI services
                  </li>
                  <li data-id="element-3942">
                    They process data according to their own privacy policies
                    and terms
                  </li>
                  <li data-id="element-3943">
                    Data may be transferred to the United States where these
                    providers operate
                  </li>
                  <li data-id="element-3944">
                    We do not control how these providers use data for model
                    training - please review their privacy policies
                  </li>
                </ul>
              </div>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-3945">
                Payment Processor (Paddle):
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3946">
                <li data-id="element-3947">
                  Payment information is collected and processed by Paddle
                </li>
                <li data-id="element-3948">
                  We receive only confirmation of payment status, not full
                  payment details
                </li>
              </ul>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-3949">
                Infrastructure Providers:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3950">
                <li data-id="element-3951">Hosting and cloud storage providers</li>
                <li data-id="element-3952">Analytics services</li>
                <li data-id="element-3953">Customer support platforms</li>
              </ul>

              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-3954">
                We share only the minimum information necessary with these
                providers, and they are contractually obligated to protect your
                data.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3955">
                3.2 Legal Requirements
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-3956">
                We may disclose your information if required to do so by law or
                in response to:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3957">
                <li data-id="element-3958">Valid legal requests from government authorities</li>
                <li data-id="element-3959">Court orders or subpoenas</li>
                <li data-id="element-3960">Legal processes or investigations</li>
                <li data-id="element-3961">Protection of our rights, property, or safety</li>
                <li data-id="element-3962">Prevention of fraud or illegal activities</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3963">
                3.3 Business Transfers
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-3964">
                If ShoPilots AI is involved in a merger, acquisition, asset
                sale, or bankruptcy, your information may be transferred as part
                of that transaction. We will notify you of any such change.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3965">
                3.4 With Your Consent
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-3966">
                We may share your information with third parties when you give
                us explicit consent to do so.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3967">
                3.5 Aggregated or De-identified Data
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-3968">
                We may share aggregated or de-identified information that cannot
                reasonably be used to identify you, without restriction.
              </p>
            </section>

            <section className="mb-10" data-id="element-3969">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-3970">
                4. Data Retention
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3971">
                4.1 Active Accounts
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-3972">
                We retain your information for as long as your account is active
                or as necessary to provide you with the Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3973">
                4.2 After Account Termination
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-3974">
                After you terminate your account:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3975">
                <li data-id="element-3976">
                  We may retain certain information for a reasonable period to
                  comply with legal obligations
                </li>
                <li data-id="element-3977">
                  We may retain backup copies for disaster recovery purposes
                </li>
                <li data-id="element-3978">
                  We may retain aggregated or de-identified data indefinitely
                </li>
                <li data-id="element-3979">
                  You may request deletion of your data by contacting us at
                  info@shopilots.com
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3980">
                4.3 Conversation Data
              </h3>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4" data-id="element-3981">
                <p className="text-gray-700 leading-relaxed mb-2" data-id="element-3982">
                  <strong data-id="element-3983">Please note:</strong> We may store chat conversations
                  and AI interactions indefinitely or for a limited time. The
                  retention period may vary based on:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700" data-id="element-3984">
                  <li data-id="element-3985">Technical requirements of our Service</li>
                  <li data-id="element-3986">Our legitimate business interests</li>
                  <li data-id="element-3987">Legal obligations</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-2" data-id="element-3988">
                  If you have concerns about conversation data retention, please
                  contact us.
                </p>
              </div>
            </section>

            <section className="mb-10" data-id="element-3989">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-3990">
                5. Data Security
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3991">
                5.1 Security Measures
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-3992">
                We implement reasonable administrative, technical, and physical
                security measures designed to protect your information,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-3993">
                <li data-id="element-3994">Encryption of data in transit and at rest</li>
                <li data-id="element-3995">Regular security assessments</li>
                <li data-id="element-3996">Access controls and authentication</li>
                <li data-id="element-3997">Secure data centers</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-3998">
                5.2 No Guarantee
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-3999">
                However, no method of transmission over the internet or
                electronic storage is 100% secure. While we strive to protect
                your information, we cannot guarantee absolute security. You
                acknowledge and accept the inherent security risks of
                internet-based services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4000">
                5.3 Your Responsibility
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4001">
                You are responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700" data-id="element-4002">
                <li data-id="element-4003">
                  Maintaining the confidentiality of your account credentials
                </li>
                <li data-id="element-4004">Choosing a strong password</li>
                <li data-id="element-4005">Logging out of your account when finished</li>
                <li data-id="element-4006">Notifying us immediately of any unauthorized access</li>
              </ul>
            </section>

            <section className="mb-10" data-id="element-4007">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4008">
                6. International Data Transfers
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4009">
                6.1 Global Service
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4010">
                ShoPilots AI operates globally and may process your information
                in countries other than where you reside, including:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4011">
                <li data-id="element-4012">Israel (where our operations are based)</li>
                <li data-id="element-4013">United States (where our AI providers are located)</li>
                <li data-id="element-4014">Other countries where our service providers operate</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4015">
                6.2 Data Protection
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-4016">
                When we transfer data internationally, we take steps to ensure
                adequate protection, though data protection laws may differ from
                your jurisdiction.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4017">
                6.3 European Users
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-4018">
                If you are located in the European Economic Area (EEA), United
                Kingdom, or Switzerland, please be aware that your data may be
                transferred to countries that do not provide the same level of
                data protection as your home country.
              </p>
            </section>

            <section className="mb-10" data-id="element-4019">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4020">
                7. Your Rights and Choices
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4021">
                7.1 Access and Correction
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4022">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4023">
                <li data-id="element-4024">Access the personal information we hold about you</li>
                <li data-id="element-4025">Correct inaccurate or incomplete information</li>
                <li data-id="element-4026">
                  Update your account information at any time through your
                  account settings
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4027">
                7.2 Data Portability
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-4028">
                You may request a copy of your data in a structured, commonly
                used format.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4029">
                7.3 Deletion
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4030">
                You may request deletion of your personal information, subject
                to:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4031">
                <li data-id="element-4032">
                  Legal obligations that require us to retain certain data
                </li>
                <li data-id="element-4033">
                  Legitimate business needs (e.g., dispute resolution, fraud
                  prevention)
                </li>
                <li data-id="element-4034">Backup retention periods</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4035">
                7.4 Opt-Out Rights
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4036">
                You may opt out of:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4037">
                <li data-id="element-4038">
                  Marketing communications (via unsubscribe link in emails)
                </li>
                <li data-id="element-4039">Certain cookies (through browser settings)</li>
                <li data-id="element-4040">
                  Certain data processing activities (contact us at
                  info@shopilots.com)
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4041">
                7.5 Do Not Track
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-4042">
                Our Service does not currently respond to "Do Not Track" browser
                signals.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4043">
                7.6 Rights for EEA/UK Users
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4044">
                If you are in the EEA or UK, you have additional rights under
                GDPR, including:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4045">
                <li data-id="element-4046">Right to object to processing</li>
                <li data-id="element-4047">Right to restrict processing</li>
                <li data-id="element-4048">Right to lodge a complaint with a supervisory authority</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4049">
                7.7 Rights for California Users
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4050">
                If you are a California resident, you have rights under CCPA,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4051">
                <li data-id="element-4052">Right to know what personal information is collected</li>
                <li data-id="element-4053">Right to deletion</li>
                <li data-id="element-4054">
                  Right to opt-out of sale (we do not sell personal information)
                </li>
                <li data-id="element-4055">Right to non-discrimination</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4056">
                7.8 Exercising Your Rights
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-4057">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:info@shopilots.com" className="text-orange-600 hover:text-orange-700 underline" data-id="element-4058">
                  info@shopilots.com
                </a>
                . We will respond to your request within a reasonable timeframe
                as required by applicable law.
              </p>
            </section>

            <section className="mb-10" data-id="element-4059">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4060">
                8. Children's Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-4061">
                Our Service is not intended for individuals under the age of 18.
                We do not knowingly collect personal information from children
                under 18.
              </p>
              <p className="text-gray-700 leading-relaxed" data-id="element-4062">
                If we learn that we have collected personal information from a
                child under 18, we will take steps to delete such information
                promptly. If you believe we have collected information from a
                child under 18, please contact us immediately at
                info@shopilots.com.
              </p>
            </section>

            <section className="mb-10" data-id="element-4063">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4064">
                9. Third-Party Links and Services
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4065">
                9.1 External Links
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-4066">
                Our Service may contain links to third-party websites or
                services that are not operated by us. We are not responsible for
                the privacy practices of these third parties.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4067">
                9.2 Third-Party AI Services
              </h3>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4" data-id="element-4068">
                <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4069">
                  <strong data-id="element-4070">Important:</strong> Our Service relies on third-party
                  AI providers (OpenAI, Anthropic Claude). These providers:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-3" data-id="element-4071">
                  <li data-id="element-4072">
                    Have their own privacy policies that govern how they handle
                    your data
                  </li>
                  <li data-id="element-4073">
                    May use your data for their own purposes, including model
                    training (subject to their policies)
                  </li>
                  <li data-id="element-4074">Operate independently of ShoPilots AI</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4075">
                  We recommend reviewing the privacy policies of:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700" data-id="element-4076">
                  <li data-id="element-4077">
                    OpenAI:{' '}
                    <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline" data-id="element-4078">
                      https://openai.com/privacy
                    </a>
                  </li>
                  <li data-id="element-4079">
                    Anthropic:{' '}
                    <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline" data-id="element-4080">
                      https://www.anthropic.com/privacy
                    </a>
                  </li>
                </ul>
              </div>
              <p className="text-gray-700 leading-relaxed" data-id="element-4081">
                We do not control and are not responsible for the privacy
                practices of these third parties.
              </p>
            </section>

            <section className="mb-10" data-id="element-4082">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4083">
                10. Cookies and Tracking Technologies
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4084">
                10.1 Types of Cookies We Use
              </h3>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-4085">
                Essential Cookies:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4086">
                <li data-id="element-4087">Required for the Service to function</li>
                <li data-id="element-4088">Enable account authentication and security features</li>
              </ul>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-4089">
                Analytics Cookies:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4090">
                <li data-id="element-4091">Help us understand how users interact with our Service</li>
                <li data-id="element-4092">Used to improve functionality and user experience</li>
              </ul>

              <p className="font-semibold text-gray-900 mb-2" data-id="element-4093">
                Preference Cookies:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4094">
                <li data-id="element-4095">Remember your settings and preferences</li>
                <li data-id="element-4096">Provide personalized experiences</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4097">
                10.2 Managing Cookies
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-4098">
                You can control cookies through your browser settings. However,
                disabling certain cookies may limit your ability to use some
                features of our Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6" data-id="element-4099">
                10.3 Third-Party Analytics
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-4100">
                We may use third-party analytics services (such as Google
                Analytics) to help us understand Service usage. These services
                may collect information about your use of our Service and other
                websites.
              </p>
            </section>

            <section className="mb-10" data-id="element-4101">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4102">
                11. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4103">
                We may update this Privacy Policy from time to time. When we do:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4104">
                <li data-id="element-4105">We will post the updated policy on this page</li>
                <li data-id="element-4106">We will update the "Last Updated" date</li>
                <li data-id="element-4107">
                  We may notify you via email or through the Service for
                  material changes
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-4108">
                Your continued use of the Service after changes constitutes
                acceptance of the updated policy.
              </p>
              <p className="text-gray-700 leading-relaxed" data-id="element-4109">
                We encourage you to review this Privacy Policy periodically to
                stay informed about how we protect your information.
              </p>
            </section>

            <section className="mb-10" data-id="element-4110">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4111">
                12. Contact Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-4112">
                If you have questions, concerns, or requests regarding this
                Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-4" data-id="element-4113">
                <div className="flex items-start gap-3 mb-3" data-id="element-4114">
                  <MailIcon className="h-5 w-5 text-orange-600 mt-0.5" data-id="element-4115" />
                  <div data-id="element-4116">
                    <p className="font-semibold text-gray-900" data-id="element-4117">Email</p>
                    <a href="mailto:info@shopilots.com" className="text-orange-600 hover:text-orange-700" data-id="element-4118">
                      info@shopilots.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-4119">
                  <div className="h-5 w-5" data-id="element-4120"></div>
                  <div data-id="element-4121">
                    <p className="font-semibold text-gray-900" data-id="element-4122">Location</p>
                    <p className="text-gray-700" data-id="element-4123">Israel</p>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mb-2" data-id="element-4124">
                For specific requests (data access, deletion, correction),
                please include:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4" data-id="element-4125">
                <li data-id="element-4126">
                  Your full name and email address associated with your account
                </li>
                <li data-id="element-4127">A detailed description of your request</li>
                <li data-id="element-4128">Any relevant account information</li>
              </ul>
              <p className="text-gray-700 leading-relaxed" data-id="element-4129">
                We will respond to your inquiry within a reasonable timeframe as
                required by applicable law.
              </p>
            </section>

            <section className="mb-10" data-id="element-4130">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4131">
                13. Data Controller
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-4132">
                For the purposes of data protection laws, ShoPilots AI is the
                data controller of your personal information.
              </p>
              <p className="text-gray-700 leading-relaxed italic" data-id="element-4133">
                Note: ShoPilots AI is currently operating as a trade name and
                does not yet have a formal legal entity. A legal entity will be
                established in the future, at which point this Privacy Policy
                will be updated accordingly to reflect the registered entity
                information.
              </p>
            </section>

            {/* Summary Box */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 mt-12" data-id="element-4134">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-4135">
                <ShieldCheckIcon className="h-6 w-6 text-purple-600" data-id="element-4136" />
                KEY PRIVACY PRINCIPLES (Summary)
              </h3>
              <p className="text-gray-700 mb-4 font-semibold" data-id="element-4137">
                Our commitment to you:
              </p>
              <ul className="space-y-2 text-gray-700 mb-6" data-id="element-4138">
                <li className="flex items-start gap-2" data-id="element-4139">
                  <span className="text-green-600 mt-1" data-id="element-4140">✓</span>
                  <span data-id="element-4141">
                    <strong data-id="element-4142">Transparency:</strong> We clearly explain what data
                    we collect and how we use it
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4143">
                  <span className="text-green-600 mt-1" data-id="element-4144">✓</span>
                  <span data-id="element-4145">
                    <strong data-id="element-4146">Limited use:</strong> We only use your data to
                    provide and improve the Service
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4147">
                  <span className="text-green-600 mt-1" data-id="element-4148">✓</span>
                  <span data-id="element-4149">
                    <strong data-id="element-4150">Third-party disclosure:</strong> We're transparent
                    about third-party AI providers and their policies
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4151">
                  <span className="text-green-600 mt-1" data-id="element-4152">✓</span>
                  <span data-id="element-4153">
                    <strong data-id="element-4154">Your control:</strong> You can access, correct, or
                    delete your data
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4155">
                  <span className="text-green-600 mt-1" data-id="element-4156">✓</span>
                  <span data-id="element-4157">
                    <strong data-id="element-4158">Security:</strong> We implement reasonable measures
                    to protect your information
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4159">
                  <span className="text-green-600 mt-1" data-id="element-4160">✓</span>
                  <span data-id="element-4161">
                    <strong data-id="element-4162">No sale of data:</strong> We do not sell your
                    personal information to third parties
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4163">
                  <span className="text-green-600 mt-1" data-id="element-4164">✓</span>
                  <span data-id="element-4165">
                    <strong data-id="element-4166">Opt-out options:</strong> You can opt out of
                    marketing and certain data uses
                  </span>
                </li>
              </ul>

              <p className="text-gray-700 mb-2 font-semibold" data-id="element-4167">
                What you should know:
              </p>
              <ul className="space-y-2 text-gray-700" data-id="element-4168">
                <li className="flex items-start gap-2" data-id="element-4169">
                  <span className="text-orange-600 mt-1" data-id="element-4170">⚠️</span>
                  <span data-id="element-4171">
                    <strong data-id="element-4172">AI provider data sharing:</strong> Your data is
                    processed by OpenAI/Anthropic according to their policies
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4173">
                  <span className="text-orange-600 mt-1" data-id="element-4174">⚠️</span>
                  <span data-id="element-4175">
                    <strong data-id="element-4176">International transfers:</strong> Data may be
                    transferred outside your country
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4177">
                  <span className="text-orange-600 mt-1" data-id="element-4178">⚠️</span>
                  <span data-id="element-4179">
                    <strong data-id="element-4180">No absolute security:</strong> Internet-based
                    services carry inherent security risks
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4181">
                  <span className="text-orange-600 mt-1" data-id="element-4182">⚠️</span>
                  <span data-id="element-4183">
                    <strong data-id="element-4184">Conversation retention:</strong> We may store AI
                    conversations indefinitely or temporarily
                  </span>
                </li>
              </ul>

              <p className="text-gray-700 mt-6 font-medium" data-id="element-4185">
                For questions or to exercise your privacy rights, contact:{' '}
                <a href="mailto:info@shopilots.com" className="text-orange-600 hover:text-orange-700 underline" data-id="element-4186">
                  info@shopilots.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default PrivacyPolicyPage;